import {
  subclass,
  property,
} from "@arcgis/core/core/accessorSupport/decorators";

import Widget from "@arcgis/core/widgets/Widget";

import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";

import "./LearnMoreSidebar.scss";
import NavBarViewModel from "../../../ui/NavBar/NavBarViewModel";
import AppT9N from "../../../../t9n/App/App.json";
import { VNode } from "src/interfaces/interfaces";

@subclass("Landing.widgets.PopoverOnboarding.LearnMoreSidebar")
export default class LearnMoreSidebar extends Widget {
  //--------------------------------------------------------------------------
  //
  //  Properties
  //
  //--------------------------------------------------------------------------

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof AppT9N;

  @property()
  navbarVM: NavBarViewModel;

  @property()
  sidebarCloseBtn: HTMLCalciteButtonElement;

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  postInitialize() {
    this.navbarVM.watch(["learnMoreSidebarSignal"], () => {
      if (this.navbarVM.learnMoreSidebarSignal === true) {
        setTimeout(() => {
          this.sidebarCloseBtn?.focus();
          this.sidebarCloseBtn?.setFocus();
        }, 250);
      }
    });

    this.navbarVM.watch(["learnMoreSidebarSelectedIndex"], () => {
      setTimeout(() => {
        this._scrollToSelectedCard();
      }, 300);
    });
  }

  render() {
    return (
      <div
        key="learn-more-sidebar-area"
        id="learn-more-sidebar-area"
        class={this.classes(
          "learn-more-sidebar-area",
          this.navbarVM.learnMoreSidebarSignal
            ? "learn-more-sidebar-area-expanded"
            : ""
        )}
      >
        {this.navbarVM.learnMoreSidebarSignal && this._renderLearnMore()}
      </div>
    );
  }

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private _renderLearnMore(): VNode {
    const { learnMore, tryIt } = this.appMessages.popoverOnboardingExtra;
    return (
      <div class="learn-more-content">
        <div class="header">
          <h3>{learnMore}</h3>
          <calcite-button
            appearance="transparent"
            icon-start="x"
            kind="neutral"
            scale="l"
            onclick={() => (this.navbarVM.learnMoreSidebarSignal = false)}
            afterCreate={(element) => (this.sidebarCloseBtn = element)}
          ></calcite-button>
        </div>
        <calcite-card-group class="tips">
          {this.navbarVM.popoverManager.viewModel.popovers.map(
            (popover, index) => {
              const { title, content, icon, badgeString } = popover;
              const chipIndex = `chipIndex${index}`;
              return (
                <calcite-card
                  class="tip"
                  key={`onboarding-tip-${index}`}
                  selected={
                    index === this.navbarVM.learnMoreSidebarSelectedIndex
                  }
                >
                  <calcite-chip
                    id={chipIndex}
                    value={badgeString}
                    icon={icon}
                    kind="brand"
                  >
                    {badgeString}
                  </calcite-chip>
                  <calcite-tooltip
                    reference-element={chipIndex}
                    placement="top"
                  >
                    {badgeString}
                  </calcite-tooltip>
                  <h4>{title}</h4>
                  <p>{content}</p>
                  <calcite-button
                    appearance="outline"
                    onclick={() => {
                      this.navbarVM.popoverManager.isClosed = false;
                      this.navbarVM.popoverManager.currentPopoverIndex = index;
                      this.navbarVM.learnMoreSidebarSelectedIndex = index;
                    }}
                  >
                    {tryIt}
                  </calcite-button>
                </calcite-card>
              );
            }
          )}
        </calcite-card-group>
      </div>
    );
  }

  private _scrollToSelectedCard() {
    const cont = document.querySelector(".learn-more-content");
    if (cont) {
      const selectedCard = cont.querySelector("calcite-card[selected]");
      if (selectedCard) {
        const contRect = cont.getBoundingClientRect();
        const cardRect = selectedCard.getBoundingClientRect();

        const contTop = contRect.top;
        const contHeight = contRect.height;

        const cardTop = cardRect.top;
        const cardBottom = cardRect.bottom;

        if (cardTop < contTop) {
          cont.scrollTo({
            top: cont.scrollTop - (contTop - cardTop),
            behavior: "smooth",
          });
        } else if (cardBottom > contTop + contHeight) {
          cont.scrollTo({
            top: cont.scrollTop + cardBottom - (contTop + contHeight),
            behavior: "smooth",
          });
        }
      }
    }
  }
}
